import { useEffect } from 'react';
import { Euler, Object3D, Vector3 } from 'three';

import { EnhancedGLTF } from '@cartwhl/3d/gltf/types';
import {
  applyFaceExpression,
  applyShowSkeleton,
  applySkinHex,
  disableFrustumCulled,
  hideMeshMaterials,
  turnOnShadows,
} from '@cartwhl/3d/gltf/utils';

import useGLTFWithCredentials from '../hooks/useGLTFWithCredentials';

export type CharacterRef = EnhancedGLTF;

/**
 * Character component that renders a character model.
 * This is a pure component that shouldn't access stores.
 * Please refer to <ActiveCharacter> component if you want to display the current project's character
 */
export default function Character({
  characterID,
  meshPath,
  rotation,
  position,
  skinHex,
  faceExpression,
  showSkeleton,
  onSceneReady,
  onCharacterLoading,
}: {
  characterID: string;
  meshPath: string;
  rotation?: Euler;
  position?: Vector3;
  skinHex?: string;
  faceExpression?: string;
  showSkeleton?: boolean;
  onSceneReady?: (scene: Object3D) => void;
  onCharacterLoading?: (loading: boolean) => void;
}) {
  const meshData = useGLTFWithCredentials(meshPath, onCharacterLoading, [
    hideMeshMaterials,
    turnOnShadows,
    disableFrustumCulled,
  ]);

  // Notify parent when scene is ready
  useEffect(() => {
    if (meshData?.scene && onSceneReady) {
      onSceneReady(meshData.scene as Object3D);
    }
  }, [meshData, onSceneReady]);

  useEffect(() => {
    if (meshData) {
      applySkinHex(skinHex, characterID)(meshData);
    }
  }, [characterID, meshData, skinHex]);

  useEffect(() => {
    if (meshData && faceExpression) {
      applyFaceExpression(faceExpression)(meshData);
    }
  }, [faceExpression, meshData]);

  useEffect(() => {
    if (meshData) {
      applyShowSkeleton(meshData, showSkeleton ?? false);
    }
  }, [showSkeleton, meshData]);

  if (!meshData) return null;

  return (
    <primitive
      object={meshData.scene}
      rotation={rotation}
      position={position}
    />
  );
}
